/**
 * One WebRTC link between two browsers — the probe's and the match's, which are the same object.
 *
 * It carries no opinion about WHAT crosses: the probe pings over it and a hosted room fans snapshots over
 * it, and both read `IcePair` off the same connection. A second implementation for the game would be
 * `arena.md §C` with the peer link as its object.
 *
 * One RELIABLE ORDERED channel (`hosted.md §12`, decision 2 · §6.2-bis).
 */
import { PendingIce, selectedPair, type IcePair } from './ice.ts';

export type PeerRole = 'offerer' | 'answerer';

export type PeerOpts = {
  role: PeerRole;
  /** What the channel is called on the wire. The probe pings on one; a room plays on another. */
  label?: string;
  /**
   * THE ICE LIST IS ASKED FOR, NOT HELD — and that is a measurement, not a preference.
   *
   * Production measured it (`ops/turn.md §5-bis`): an expired TURN credential does NOT cut a relay
   * already running, but it DOES refuse a new allocation. An ICE restart asks for a new one, so a restart
   * that reuses the list it was built with is a restart with no relay to fall into — on the pair whose
   * path just failed, which is exactly the pair that needs one.
   */
  ice(): Promise<RTCIceServer[]>;
  /** Send one signalling frame to the other side. Returns when the backend has accepted it. */
  send(kind: 'offer' | 'answer' | 'ice', payload: unknown): Promise<void>;
  onNote(text: string): void;
  onState(state: string): void;
  /**
   * The channel is open and this is how it got through.
   *
   * `signalMs` is the SIGNALLING half — offer out to answer in — split from the total because the
   * threshold is on the total and the two halves have nothing to do with each other: one is our own
   * relay's round trips, the other is ICE. A single number cannot say which one to go and fix.
   */
  onUp(pair: IcePair | null, connectMs: number, signalMs: number): void;
  /** What arrived on the channel, in the form it arrived in. Bytes when the sender compressed. */
  onData(data: string | ArrayBuffer): void;
  /** The channel went away for good. The room reads this as the peer leaving. */
  onDown?(): void;
};

export class Peer {
  readonly pc: RTCPeerConnection;
  private ch: RTCDataChannel | null = null;
  private readonly pending: PendingIce;
  private startedAt = 0;
  /** When the remote description landed — the boundary between the two halves above. */
  private remoteAt = 0;
  private opened = false;
  private restarted = false;

  /**
   * BUILT THROUGH HERE because the ICE list is fetched, and a constructor cannot await.
   *
   * The alternative — a list passed in beside `ice()` — would be the same list described twice, and the
   * copy that aged would be whichever one the caller happened to hold.
   */
  static async create(o: PeerOpts): Promise<Peer> {
    return new Peer(o, await o.ice());
  }

  private constructor(private readonly o: PeerOpts, iceServers: RTCIceServer[]) {
    this.pc = new RTCPeerConnection({ iceServers });
    this.pending = new PendingIce(this.pc);

    this.pc.onicecandidate = (e) => {
      // TRICKLE, and it is not optional: Reverb refuses an inbound broadcast over 10.000 B
      // (`backend/config/reverb.php`), and an SDP carrying every candidate is how that ceiling gets hit.
      if (e.candidate) void o.send('ice', e.candidate.toJSON());
    };
    this.pc.oniceconnectionstatechange = () => {
      const s = this.pc.iceConnectionState;
      o.onState(s);
      // Recovery, which did not exist anywhere in this product (`hosted.md §4` item 13). ONCE: a page
      // that restarts ICE on every flap turns a hiccup into a gathering storm, and the second failure
      // means the path is gone rather than jittering — a fact to report, not to retry.
      if ((s === 'failed' || s === 'disconnected') && !this.restarted && typeof this.pc.restartIce === 'function') {
        this.restarted = true;
        o.onNote(`ICE went ${s} — restarting it once`);
        void this.restart();
      }
    };
    this.pc.ondatachannel = (e) => this.adopt(e.channel);
  }

  /**
   * A FRESH LIST BEFORE A FRESH GATHERING, then the restart.
   *
   * `setConfiguration` is what a live connection accepts — replacing it would drop the very session being
   * rescued. Failing to fetch is not fatal: restarting with the old list still recovers every pair that
   * did not need a relay, and refusing to restart at all would abandon those too.
   */
  private async restart(): Promise<void> {
    try {
      this.pc.setConfiguration({ iceServers: await this.o.ice() });
    } catch (err) {
      this.o.onNote(`could not refresh the ICE list before restarting — ${String(err)}`);
    }
    this.pc.restartIce();
    if (this.o.role === 'offerer') await this.offer();
  }

  /** The offerer creates the channel; the answerer receives it. */
  async offer(): Promise<void> {
    if (!this.ch) {
      this.startedAt = performance.now();
      this.adopt(this.pc.createDataChannel(this.o.label ?? 'probe', { ordered: true }));
    }
    const offer = await this.pc.createOffer({ iceRestart: this.restarted });
    await this.pc.setLocalDescription(offer);
    await this.o.send('offer', { type: offer.type, sdp: offer.sdp ?? '' });
  }

  async onOffer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.startedAt) this.startedAt = performance.now();
    await this.pc.setRemoteDescription(sdp);
    this.remoteAt = performance.now();
    await this.pending.remoteReady();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    await this.o.send('answer', { type: answer.type, sdp: answer.sdp ?? '' });
  }

  async onAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    // A second answer for a description already applied is a renegotiation crossing on the wire, not a
    // fault: applying it over a stable connection is what would break the one that works.
    if (this.pc.signalingState !== 'have-local-offer') return;
    await this.pc.setRemoteDescription(sdp);
    this.remoteAt = performance.now();
    await this.pending.remoteReady();
  }

  onIce(c: RTCIceCandidateInit): Promise<void> {
    return this.pending.add(c);
  }

  /** True while the channel can carry a frame. Asked before every write; a room outlives a flap. */
  get open(): boolean {
    return this.ch?.readyState === 'open';
  }

  /**
   * Bytes queued on this channel and not yet on the wire — the back-pressure the sender has to read.
   *
   * The guest side has checked it since §22.2; the host side had nothing, so eleven writes per tick into
   * a congested peer grew without a ceiling (`hosted.md §6.2`).
   */
  get buffered(): number {
    return this.ch?.bufferedAmount ?? 0;
  }

  /** Dropped when the channel is not open, on purpose: a room mid-renegotiation is not a room over, and
   *  queueing frames for it would deliver a burst of stale world the moment it came back. */
  send(payload: string | ArrayBuffer): void {
    const ch = this.ch;
    if (ch?.readyState !== 'open') return;
    if (typeof payload === 'string') ch.send(payload);
    else ch.send(payload);
  }

  get droppedIce(): number {
    return this.pending.dropped;
  }

  close(): void {
    try {
      this.ch?.close();
      this.pc.close();
    } catch {
      // Already gone.
    }
  }

  private adopt(ch: RTCDataChannel): void {
    this.ch = ch;
    // Or a compressed frame arrives as a `Blob` and every reader has to await it. The host deflates per
    // frame (`host/coder.ts`), so binary is the normal case here and not the exception.
    ch.binaryType = 'arraybuffer';
    ch.onopen = () => {
      if (this.opened) return;
      this.opened = true;
      const ms = Math.round(performance.now() - this.startedAt);
      const signalMs = this.remoteAt ? Math.round(this.remoteAt - this.startedAt) : 0;
      // Read AFTER the channel opens, never before: until then the selected pair is a pair ICE is still
      // considering, and reporting one is answering a question that has not been decided yet.
      void selectedPair(this.pc).then((pair) => this.o.onUp(pair, ms, signalMs));
    };
    ch.onclose = () => {
      this.o.onNote('the data channel closed');
      this.o.onDown?.();
    };
    ch.onmessage = (ev) => this.o.onData(ev.data as string | ArrayBuffer);
  }
}
